import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../config";
import carbelimLogo from "../assets/carbelim-logo.png";

const Login = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    remember: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= INPUT ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });

    setError("");
  };

  /* ================= ROLE REDIRECT ================= */
  const redirectUser = (user) => {
    const role = user.role?.toLowerCase().replace(/\s+/g, "_").trim();

    if (role === "esg_admin") {
      navigate("/dashboard/esg-reports");
    } else if (role === "company_admin") {
      navigate("/dashboard/devices");
    } else {
      // super_admin + system_manager + fallback
      navigate("/dashboard");
    }
  };

  /* ================= LOGIN ================= */
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!form.email || !form.password) {
      setError("Email and password are required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: form.email,
        password: form.password,
      });

      const { token, user } = res.data;

      if (!token || !user) {
        throw new Error("Invalid login response");
      }

      /* 🔥 CLEAR OLD SESSION FIRST (important) */
      localStorage.clear();
      sessionStorage.clear();

      /* 🔥 STORE (single source of truth) */
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      /* OPTIONAL: store remember flag */
      localStorage.setItem("remember", form.remember ? "true" : "false");

      console.log("✅ Login success:", user);

      /* 🔥 REDIRECT */
      redirectUser(user);

    } catch (err) {
      console.error("Login Error:", err);

      setError(
        err.response?.data?.message ||
        err.message ||
        "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#f3f7f5] text-slate-950">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_18%_12%,rgba(22,163,74,0.18),transparent_30%),radial-gradient(circle_at_86%_8%,rgba(37,99,235,0.12),transparent_26%)]" />
      <div className="relative grid min-h-screen grid-cols-1 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="hidden flex-col justify-between px-12 py-10 lg:flex">
          <div className="flex items-center gap-3">
            <img src={carbelimLogo} alt="Carbelim" className="h-12 w-12 rounded-lg bg-white p-2 shadow-sm ring-1 ring-emerald-100" />
            <div>
              <p className="text-xl font-black">Carbelim</p>
              <p className="text-xs font-bold uppercase text-emerald-700">IoT Platform</p>
            </div>
          </div>

          <div className="max-w-xl">
            <p className="mb-4 inline-flex rounded-full border border-emerald-200 bg-white/70 px-3 py-1 text-xs font-bold text-emerald-700 shadow-sm">
              Live device operations
            </p>
            <h1 className="text-5xl font-black leading-tight tracking-tight">
              Premium control room for water, air, and sustainability devices.
            </h1>
            <p className="mt-5 text-base font-medium leading-7 text-slate-600">
              Monitor telemetry, configure products, manage relays, export reports, and keep every site visible from one polished workspace.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-3">
              {["Telemetry", "Reports", "Relay Ops"].map((item) => (
                <div key={item} className="rounded-lg border border-white/70 bg-white/75 p-4 shadow-xl shadow-slate-900/5 backdrop-blur">
                  <div className="mb-3 h-1.5 w-10 rounded-full bg-emerald-500" />
                  <p className="text-sm font-bold text-slate-900">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs font-semibold text-slate-400">Carbelim Sustainability Platform</p>
        </section>

        <section className="flex items-center justify-center px-6 py-10">
      <form
        onSubmit={handleLogin}
            className="w-full max-w-md space-y-5 rounded-lg border border-white/80 bg-white/90 p-8 shadow-2xl shadow-slate-900/10 backdrop-blur"
      >
        {/* HEADER */}
            <div className="text-center lg:text-left">
              <img src={carbelimLogo} alt="Carbelim" className="mx-auto mb-5 h-12 w-12 rounded-lg bg-emerald-50 p-2 ring-1 ring-emerald-100 lg:mx-0" />
              <h2 className="text-2xl font-black text-slate-950">
                Welcome back
              </h2>
              <p className="mt-1 text-sm font-medium text-slate-500">
            Access your IoT dashboard
          </p>
        </div>

        {/* ERROR */}
        {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-600">
            {error}
          </div>
        )}

        {/* EMAIL */}
        <input
          type="email"
          name="email"
          placeholder="Email"
          required
              className="w-full rounded-lg border border-slate-200 bg-white p-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          value={form.email}
          onChange={handleChange}
        />

        {/* PASSWORD */}
        <input
          type="password"
          name="password"
          placeholder="Password"
          required
              className="w-full rounded-lg border border-slate-200 bg-white p-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          value={form.password}
          onChange={handleChange}
        />

        {/* OPTIONS */}
            <div className="flex items-center justify-between text-sm text-slate-500">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="remember"
              checked={form.remember}
              onChange={handleChange}
            />
            Remember me
          </label>

          <span
            onClick={() => navigate("/forgot-password")}
                className="cursor-pointer font-semibold text-emerald-700 hover:underline"
          >
            Forgot Password?
          </span>
        </div>

        {/* BUTTON */}
        <button
          type="submit"
          disabled={loading}
              className={`w-full rounded-lg py-3 font-semibold transition ${
            loading
                  ? "cursor-not-allowed bg-slate-300 text-white"
                  : "bg-emerald-600 text-white shadow-lg shadow-emerald-700/20 hover:bg-emerald-700"
          }`}
        >
          {loading ? "Signing in..." : "Login"}
        </button>

        <div className="text-center text-xs font-semibold text-slate-500">
          By continuing, you agree to Carbelim data handling.{" "}
          <button
            type="button"
            onClick={() => navigate("/privacy-policy")}
            className="font-bold text-emerald-700 hover:underline"
          >
            Privacy Policy
          </button>
        </div>
      </form>
        </section>
      </div>
    </div>
  );
};

export default Login;
